// Besin günlüğü (2026-10-05) — hastanın mobil uygulamada girdiği öğünler
// (yalnız metin). Diyetisyen gün gün görür ve yorum bırakır;
// yorumda hastaya içeriksiz push gider. Düz liste: gün başlıkları + kayıt satırları.

import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { NoMeals as FoodLogIcon } from "@mui/icons-material";

import EmptyState from "../../atoms/EmptyState";
import FormTextField from "../../atoms/FormTextField";
import ActionButton from "../../atoms/ActionButton";
import ChoiceChipGroup from "../../atoms/ChoiceChipGroup";
import { WORKSPACE_ROOT_SX, WorkspaceBody, WorkspaceSkeleton } from "./_workspaceParts";
import { PdfButton } from "./_editorParts";
import { useClinicTheme } from "../../../theme";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useAlert } from "../../../context/AlertProvider";
import {
  useListPatientFoodLogsMutation,
  useCommentFoodLogMutation,
  useGetIntakeSummaryMutation,
} from "../../../store/secretaryApi";
import IntakeSummary from "../DietPlan/IntakeSummary";
import { MEAL_LABEL } from "../DietPlan/dietPlanFormat";
import useTerms from "../../../hooks/useTerms";

// @react-pdf + fontlar ayrı chunk (bkz. TreatmentEditor)
const FoodLogPreview = lazy(() => import("../pdf/FoodLogPreview"));

const PERIODS = [
  { value: "7", label: "7 gün" },
  { value: "14", label: "14 gün" },
  { value: "30", label: "30 gün" },
];

const dayKey = (d) => new Date(d).toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long" });
const timeOf = (d) => new Date(d).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
const nameOf = (u) => (u ? `${u.title ? `${u.title} ` : ""}${u.name || ""} ${u.surname || ""}`.trim() : "");

function LogRow({ log, palette, canComment, onComment }) {
  const terms = useTerms();
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ display: "flex", gap: 2, px: { xs: 2, md: 4 }, py: 1.5, borderBottom: `1px solid ${COLORS.slate100}` }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate800 }}>
          <Box component="span" sx={{ fontWeight: 600 }}>{MEAL_LABEL[log.mealType] || log.mealType}</Box>
          <Box component="span" sx={{ color: COLORS.slate500, ml: 1, fontSize: TYPO.label }}>{timeOf(log.loggedAt)}</Box>
        </Typography>
        {log.text ? (
          <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700, whiteSpace: "pre-wrap", mt: 0.25 }}>{log.text}</Typography>
        ) : null}
        {(log.comments || []).map((c) => (
          <Typography key={c._id} sx={{ fontSize: TYPO.label, color: COLORS.slate600, mt: 0.75, pl: 1.5, borderLeft: `2px solid ${palette.primary}` }}>
            {c.text}
            <Box component="span" sx={{ color: COLORS.slate400, ml: 1 }}>— {nameOf(c.by)}</Box>
          </Typography>
        ))}
        {canComment && !open && (
          <Typography
            component="button"
            onClick={() => setOpen(true)}
            sx={{ mt: 0.75, p: 0, border: 0, bgcolor: "transparent", cursor: "pointer", fontSize: TYPO.label, color: palette.primary }}
          >
            Yorum yaz
          </Typography>
        )}
        {open && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end", mt: 1 }}>
            <FormTextField
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              multiline
              placeholder={`${terms.patientDat} kısa geri bildirim`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <ActionButton
              variant="send"
              compact
              palette={palette}
              disabled={!draft.trim()}
              onClick={async () => {
                const ok = await onComment(log, draft.trim());
                if (ok) {
                  setDraft("");
                  setOpen(false);
                }
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * @param {React.ReactNode} [hydrationSlot] - Beslenme sekmesinde hastanın su takibi
 *   (günlük alımın altında, öğün notlarının üstünde)
 */
export default function FoodLogTab({ patientId, patient, token, hydrationSlot }) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const { showError, showSuccess } = useAlert();
  const canComment = useHasPermission("diet:update");
  const [listLogs, { isLoading }] = useListPatientFoodLogsMutation();
  const [comment] = useCommentFoodLogMutation();
  const [getIntake, { isLoading: intakeLoading }] = useGetIntakeSummaryMutation();
  const [intake, setIntake] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [days, setDays] = useState("14");
  const [printing, setPrinting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await listLogs({ token, patientId, days: Number(days) }).unwrap();
      setLogs(res?.data?.logs || []);
    } catch (_) {
      showError("Besin günlüğü yüklenemedi.");
    } finally {
      setLoaded(true);
    }
  }, [listLogs, token, patientId, days, showError]);

  useEffect(() => {
    if (token && patientId) refresh();
  }, [token, patientId, refresh]);

  useEffect(() => {
    if (!token || !patientId) return;
    getIntake({ token, patientId, days: Number(days) })
      .unwrap()
      .then((r) => setIntake(r?.data || null))
      .catch(() => setIntake(null));
  }, [token, patientId, days, getIntake]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const l of logs) {
      const k = dayKey(l.loggedAt);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(l);
    }
    return [...map.entries()];
  }, [logs]);

  const handleComment = async (log, text) => {
    try {
      const res = await comment({ token, patientId, logId: log._id, text }).unwrap();
      const updated = res?.data?.log;
      if (updated) setLogs((prev) => prev.map((l) => (l._id === updated._id ? updated : l)));
      showSuccess("Yorum gönderildi.");
      return true;
    } catch (err) {
      showError(err?.data?.message || "Yorum gönderilemedi.");
      return false;
    }
  };

  return (
    <Box sx={WORKSPACE_ROOT_SX}>
      <WorkspaceBody
        title="Besin Günlüğü"
        count={logs.length}
        action={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ChoiceChipGroup options={PERIODS} value={days} onChange={setDays} palette={palette} ariaLabel="Dönem" />
            <PdfButton onClick={() => setPrinting(true)} title="Besin günlüğü çıktısı (PDF)" palette={palette} />
          </Box>
        }
      >
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {/* Günlük alım: hastanın mobilde işaretledikleri ve girdiği besinler */}
          <Box sx={{ px: { xs: 2, md: 4 }, py: 2, borderBottom: `1px solid ${COLORS.slate100}` }}>
            <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800, mb: 1.5 }}>Günlük alım</Typography>
            <IntakeSummary data={intake} loading={intakeLoading} palette={palette} />
          </Box>

          {hydrationSlot ? (
            <Box sx={{ px: { xs: 2, md: 4 }, py: 2, borderBottom: `1px solid ${COLORS.slate100}` }}>
              <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800, mb: 1.5 }}>Su</Typography>
              {hydrationSlot}
            </Box>
          ) : null}

          <Typography
            sx={{ px: { xs: 2, md: 4 }, pt: 2, pb: 1, fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }}
          >
            Öğün notları
          </Typography>
          {!loaded && isLoading ? (
            <WorkspaceSkeleton />
          ) : logs.length === 0 ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
              <EmptyState
                variant="illustrated-sm"
                icon={<FoodLogIcon />}
                title="Bu dönemde öğün notu yok"
                description={`${terms.patient} mobil uygulamada öğün notu yazdığında burada gün gün görünür.`}
              />
            </Box>
          ) : (
            groups.map(([day, items]) => (
              <Box key={day}>
                <Typography
                  sx={{ px: { xs: 2, md: 4 }, py: 1, bgcolor: COLORS.slate50, fontSize: TYPO.caption, color: COLORS.slate500, textTransform: "uppercase", letterSpacing: "0.04em" }}
                >
                  {day} · {items.length} not
                </Typography>
                {items.map((l) => (
                  <LogRow key={l._id} log={l} palette={palette} canComment={canComment} onComment={handleComment} />
                ))}
              </Box>
            ))
          )}
        </Box>
      </WorkspaceBody>
      {printing && (
        <Suspense fallback={null}>
          <FoodLogPreview
            open={printing}
            onClose={() => setPrinting(false)}
            patient={patient}
            intake={intake}
            logs={logs}
            days={Number(days)}
          />
        </Suspense>
      )}
    </Box>
  );
}
